import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { PROGRESSION_DECKS, deckAvatarCard, deckAvatarImageUrl } from '../utils/arena/starterDecks';
import { UI } from '../utils/arena/uiSounds';
import {
  GOLD, TEXT_MUTED, ACCENT_GOLD, PARCHMENT,
  BG_ATMOSPHERE, VIGNETTE, GOLD_BTN, BEVELED_BTN,
  CONTENT_BG_DEFAULT, OrnamentalDivider, RADIUS,
} from '../lib/medievalTheme';
import CardInspector from './CardInspector';
import CardFlipButton from './CardFlipButton';
import ElementIcon from './deckStats/ElementIcon';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { stopMusic, playMusic, getCurrentTrack } from '../utils/arena/musicManager';
import { stopAmbience, playAmbience, getAmbienceState } from '../utils/arena/ambienceManager';
import { getEffectiveMusicVolume } from '../utils/arena/soundSettings';
import { getLocalApiOrigin } from '../utils/localApi';
import { t } from '../localization/t';

// Each deck plays its own thematic ambience track while it's the
// featured selection in the picker. Two decks (Flamecaller and
// Persecutor) share 'dungeon' deliberately. They're never on stage
// simultaneously, and both read as forge/zealot-heat atmospheres.
const PICKER_AMBIENCE_BY_DECK = {
  flamecaller: 'snd-ambience-dungeon.mp3',
  geomancer:   'snd-ambience-forest.mp3',
  waveshaper:  'snd-ambience-underwater.mp3',
  sparkmage:   'snd-ambience-snowy-mountain.mp3',
  savior:      'snd-ambience-pasture.mp3',
  necromancer: 'snd-ambience-cave.mp3',
  persecutor:  'snd-ambience-dungeon.mp3',
  harbinger:   'snd-ambience-swamp.mp3',
};

const ELEMENT_COLORS = {
  Earth: { color: '#D5AA75', bg: 'rgba(213,170,117,0.14)', border: 'rgba(213,170,117,0.45)', glow: 'rgba(213,170,117,0.35)', rgb: '213,170,117' },
  Water: { color: '#02FFFF', bg: 'rgba(2,255,255,0.14)', border: 'rgba(2,255,255,0.45)', glow: 'rgba(2,255,255,0.35)', rgb: '2,255,255' },
  Fire: { color: '#e05040', bg: 'rgba(224,80,64,0.14)', border: 'rgba(224,80,64,0.45)', glow: 'rgba(224,80,64,0.35)', rgb: '224,80,64' },
  Air: { color: '#7cb8d4', bg: 'rgba(124,184,212,0.14)', border: 'rgba(124,184,212,0.45)', glow: 'rgba(124,184,212,0.35)', rgb: '124,184,212' },
};

// Element-themed aura behind the hero avatar. CIRCLE radial. Not
// ellipse. So the gradient never adapts to the container's aspect
// ratio; the blob is always round regardless of container shape, and
// can't show its bounds as a pulsing rectangle when the breathe
// keyframe scales. Sized with `closest-side` so the visible falloff
// happens well inside the container's bounds (a safety margin against
// any unknown parent clipping). Dual-element decks split into two
// offset circles so both colors stay identifiable.
function elementHaloGradient(elements) {
  if (!elements?.length) {
    return 'radial-gradient(circle closest-side at center, rgba(180,140,60,0.5) 0%, rgba(180,140,60,0.18) 50%, transparent 100%)';
  }
  if (elements.length === 1) {
    const rgb = ELEMENT_COLORS[elements[0]]?.rgb || '180,140,60';
    return `radial-gradient(circle closest-side at center, rgba(${rgb},0.6) 0%, rgba(${rgb},0.2) 50%, transparent 100%)`;
  }
  const rgb1 = ELEMENT_COLORS[elements[0]]?.rgb || '180,140,60';
  const rgb2 = ELEMENT_COLORS[elements[1]]?.rgb || '180,140,60';
  return [
    `radial-gradient(circle closest-side at 35% 50%, rgba(${rgb1},0.6) 0%, rgba(${rgb1},0.18) 55%, transparent 100%)`,
    `radial-gradient(circle closest-side at 65% 50%, rgba(${rgb2},0.6) 0%, rgba(${rgb2},0.18) 55%, transparent 100%)`,
  ].join(', ');
}

// Keyframes injected once via inline <style> below. The breathe is
// slow (~3.8s) and the scale range is tiny (0.96 → 1.05) so it reads
// as ambient torch flicker, not a focal animation that competes with
// the hero card.
const PICKER_KEYFRAMES = `
@keyframes aspHaloBreathe {
  0%, 100% { opacity: 0.7; transform: scale(0.96); }
  50%      { opacity: 1;   transform: scale(1.05); }
}
`;

/**
 * Unified deck picker. Handles both the first-run starter pick and
 * the level-milestone progression picks. Props:
 *
 *   decks. Array of precon decks to show (defaults to the
 *                     full 8-deck PROGRESSION_DECKS pool).
 *   ownedIds. Ids of decks the player already owns. Rendered
 *                     greyed-out with a checkmark; can't be selected.
 *   title/subtitle. Overrides for the header copy.
 *   confirmLabel. Overrides for the confirm button text.
 *   onChoose. Called with the selected deck object on confirm.
 *   onStarterChosen. Legacy alias for onChoose, kept so existing
 *                     callers don't have to migrate in the same commit.
 */
export default class ArenaStarterPicker extends Component {
  constructor(props) {
    super(props);
    // Pre-select the first non-owned deck so the stage is populated
    // the moment the screen mounts. No "blank stage waiting for first
    // click" state. Mirrors how Elden Ring's class select opens on a
    // default class with full lore visible.
    const decks = props.decks || PROGRESSION_DECKS;
    const ownedIds = new Set(props.ownedIds || []);
    const firstAvailable = decks.find((d) => !ownedIds.has(d.id));
    this.state = {
      selected: firstAvailable ? firstAvailable.id : null,
      entryDone: false,
      hoveredKeyCard: null,
      hoveredAvatar: false,
      inspectedCard: null,
      // Per-deck face state for two-sided avatars (Druid etc.). Keyed
      // by deck id so the front/back choice survives navigating away
      // and coming back to the same deck, while a different deck
      // resets to its own face.
      avatarFace: {},
    };
    // Tap-vs-hold tracking for the Space hotkey, same recipe Mailbox /
    // ChatView use. Lets a quick tap of Space toggle the inspector open
    // (then tap again to close), while holding Space keeps it open
    // until release. Also handles the "pre-arm" case: pressing Space
    // with nothing hovered queues an open for the next hover.
    this.inspectorHold = new InspectorHoldTracker();
    setTimeout(() => this.setState({ entryDone: true }), 600);
  }

  // Active fade intervals + every Audio element this instance ever
  // created. Tracked together so componentWillUnmount can hard-stop
  // both. Otherwise a rapid remount (the queue-mode flow: milestone
  // claim → unmount → 600ms gap → next popup → click → remount)
  // leaves the previous instance's audio elements mid-fade in the
  // browser's media-element pool, and on some platforms the new
  // mount's audio.play() silently no-ops while the orphans drain.
  _fadeIntervals = new Set();
  _allAudios = new Set();

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    // Snapshot the audio state BEFORE we tear it down so the unmount
    // path can restore exactly what was playing. Hub music + the
    // user's ambience pref both need to resume when the picker
    // closes. Otherwise dismissing the picker leaves the player on
    // a silent hub.
    this._prevMusicTrack = getCurrentTrack();
    this._prevAmbiencePlaying = getAmbienceState().isPlaying;

    // Take over the audio channel. Any background music from the
    // ArenaHub etc. fades out, the user's persistent ambience is
    // silenced without touching its persisted play preference (so
    // closing the app mid-picker won't forget the user's choice),
    // and the picker's own deck-themed ambience takes the stage.
    stopMusic(600);
    stopAmbience({ silent: true });
    this.startPickerAmbience(this.state.selected);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    removeDismiss(this._escDismissId);
    // Hard-stop everything synchronously. We previously called
    // stopPickerAmbience(500) and let the fade run after unmount,
    // but that left audio elements captured in interval closures
    // alive for the full fade duration, contending with the next
    // picker mount for the browser's media element pool.
    for (const id of this._fadeIntervals) clearInterval(id);
    this._fadeIntervals.clear();
    for (const a of this._allAudios) {
      try { a.pause(); } catch {}
      a.src = '';
    }
    this._allAudios.clear();
    this._pickerAudio = null;

    // Restore the audio state that was playing before the picker
    // took over. Music re-fades-in over the standard 3s; ambience
    // resumes via the manager's own fade if the user had it on.
    // Wrapped in a try/catch since restoring audio shouldn't ever
    // block the unmount path.
    try {
      if (this._prevMusicTrack) {
        playMusic(this._prevMusicTrack, { fadeInDuration: 3000 });
      }
      if (this._prevAmbiencePlaying) {
        playAmbience();
      }
    } catch (err) {
      console.warn('[picker] audio restore failed:', err);
    }
  }

  // Consume an armed-Space when the cursor enters a card/avatar after
  // the keypress. Mirrors Mailbox's componentDidUpdate hook so the
  // "press Space first, hover after" interaction works the same way.
  componentDidUpdate(_prevProps, prevState) {
    const beganHover = (
      (this.state.hoveredKeyCard && !prevState.hoveredKeyCard)
      || (this.state.hoveredAvatar && !prevState.hoveredAvatar)
    );
    if (beganHover && !this.state.inspectedCard && this.inspectorHold.consumePending()) {
      const card = this.resolveHoveredInspectCard();
      if (card) this.setState({ inspectedCard: card });
    }
    // Cross-fade to the new deck's ambience whenever the selection
    // changes. Clicking a thumbnail or arrowing through the strip.
    if (prevState.selected !== this.state.selected) {
      this.startPickerAmbience(this.state.selected);
    }
    // Auto-advance the selection when the current pick has become
    // owned. The picker instance survives across consecutive milestone
    // claims (the parent keeps it mounted while pendingDeckGrants > 0),
    // so without this the hero stage would keep featuring the deck
    // the player just claimed and the confirm button would re-submit
    // the same id. Which the server then rejects.
    const ownedIds = new Set(this.props.ownedIds || []);
    if (this.state.selected && ownedIds.has(this.state.selected)) {
      const decks = this.props.decks || PROGRESSION_DECKS;
      const next = decks.find((d) => !ownedIds.has(d.id));
      this.setState({ selected: next ? next.id : null });
    }

    const inspectorOpen = this.state.inspectedCard != null;
    const wasInspectorOpen = prevState.inspectedCard != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => this.setState({ inspectedCard: null }));
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  // Start (or cross-fade to) the ambience track that fits the selected
  // deck. Always recreates the audio element, even if the target file
  // matches what we think is currently playing. The brittleness we
  // were avoiding (a same-file early-return) was hiding a worse
  // problem: when a click on a thumbnail happens shortly after the
  // picker remounts in queue mode, a stale `_pickerAudio` could pass
  // the dataset.file equality check but actually be in a paused or
  // detached state, silently swallowing the click. Forcing a fresh
  // element guarantees the click-triggered ambience always plays.
  startPickerAmbience(deckId) {
    const file = PICKER_AMBIENCE_BY_DECK[deckId];
    if (!file) {
      this.stopPickerAmbience(400);
      return;
    }
    const previous = this._pickerAudio;
    if (previous) this.fadePickerAudio(previous, previous.volume, 0, 400, () => {
      try { previous.pause(); } catch {}
      previous.src = '';
      this._allAudios.delete(previous);
    });

    const targetVolume = getEffectiveMusicVolume();
    if (targetVolume <= 0) {
      this._pickerAudio = null;
      return;
    }
    const audio = new Audio(`${getLocalApiOrigin()}/game-assets/${file}`);
    audio.loop = true;
    audio.dataset.file = file;
    audio.volume = 0;
    this._pickerAudio = audio;
    this._allAudios.add(audio);
    this._attemptPickerAudioPlay(audio);
    this.fadePickerAudio(audio, 0, targetVolume, 600);
  }

  // Aggressive play retry: an initial play() failure (autoplay block,
  // race with rapid remount, brief media-element pool contention)
  // installs BOTH a one-shot gesture listener AND a short polling
  // interval. The poll covers cases where no further user gesture
  // arrives. Common when the user just clicked the level-up popup's
  // "Claim Deck" button and is now waiting for the picker to settle
  // before clicking a thumbnail. Mirrors musicManager's recipe.
  _attemptPickerAudioPlay(audio) {
    audio.play().catch(() => {
      let pollId = null;
      const tryAgain = () => {
        if (this._pickerAudio !== audio) { cleanup(); return; }
        if (!audio.paused) { cleanup(); return; }
        audio.play().then(() => cleanup()).catch(() => {});
      };
      const cleanup = () => {
        if (pollId != null) { clearInterval(pollId); pollId = null; }
        document.removeEventListener('pointerdown', tryAgain);
        document.removeEventListener('keydown', tryAgain);
      };
      pollId = setInterval(tryAgain, 500);
      document.addEventListener('pointerdown', tryAgain, { once: true });
      document.addEventListener('keydown', tryAgain, { once: true });
      // Also stop polling after 5s so we don't leak forever if play
      // is permanently denied.
      setTimeout(() => cleanup(), 5000);
    });
  }

  stopPickerAmbience(durationMs = 500) {
    const audio = this._pickerAudio;
    if (!audio) return;
    this._pickerAudio = null;
    this.fadePickerAudio(audio, audio.volume, 0, durationMs, () => {
      try { audio.pause(); } catch {}
      audio.src = '';
      this._allAudios.delete(audio);
    });
  }

  // Linear-eased fade for a single Audio element. Tracks the interval
  // id on the instance so componentWillUnmount can cancel any in-
  // flight fades when the picker is torn down. Otherwise the
  // interval keeps ticking against a detached audio element until
  // its own self-clear, which on rapid remount (queue-mode flow)
  // can hold the media element pool in a half-released state.
  fadePickerAudio(audio, fromVol, toVol, durationMs, onDone) {
    const steps = Math.max(8, Math.round(durationMs / 25));
    const stepTime = durationMs / steps;
    let i = 0;
    const id = setInterval(() => {
      i++;
      if (i >= steps) {
        audio.volume = toVol;
        clearInterval(id);
        this._fadeIntervals.delete(id);
        if (onDone) onDone();
        return;
      }
      const t = i / steps;
      audio.volume = fromVol + (toVol - fromVol) * t;
    }, stepTime);
    this._fadeIntervals.add(id);
  }

  // Whichever card is "under inspection focus" right now. Key card
  // beats avatar if both are hovered (the cursor is physically on top
  // of the key card). Returns the full sorcery card object so we can
  // hand it straight to CardInspector.
  resolveHoveredInspectCard() {
    const cards = this.props.sorceryCards;
    if (!cards) return null;
    if (this.state.hoveredKeyCard) {
      return cards.find((c) => c.name === this.state.hoveredKeyCard) || null;
    }
    if (this.state.hoveredAvatar) {
      const decks = this.props.decks || PROGRESSION_DECKS;
      const selectedDeck = decks.find((d) => d.id === this.state.selected);
      if (!selectedDeck) return null;
      return cards.find((c) => c.name === selectedDeck.name && c.type === 'Avatar') || null;
    }
    return null;
  }

  handleKeyDown = (e) => {
    if (e.repeat) return;
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;

    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      if (this.state.inspectedCard) {
        this.inspectorHold.noteClose();
        this.setState({ inspectedCard: null });
      } else {
        const card = this.resolveHoveredInspectCard();
        if (card) {
          this.inspectorHold.noteOpen();
          this.setState({ inspectedCard: card });
        } else {
          this.inspectorHold.noteArmed();
        }
      }
    }
  };

  handleKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    if (this.state.inspectedCard) {
      // Inspector open: close it if Space was held past the threshold.
      // Quick taps fall through and leave the inspector open
      // (tap-toggle mode).
      if (this.inspectorHold.shouldCloseOnKeyUp()) {
        e.preventDefault();
        this.setState({ inspectedCard: null });
      }
      return;
    }
    // Inspector NOT open: clear any pre-armed pending state so a
    // stray Space tap with no hover doesn't trigger an unintended
    // open the next time the cursor crosses a card. Without this,
    // _pending sits forever and the next hover consumes it.
    this.inspectorHold.noteClose();
  };

  getAvatarCard(deck) {
    return deckAvatarCard(deck, this.props.sorceryCards);
  }

  // Front face follows the art the deck actually grants (the Gothic decks
  // grant the precon alt illustration). Back faces exist on booster art only,
  // so a flip falls back to the base printing.
  getAvatarImageUrl(deck, face = 'front') {
    const card = this.getAvatarCard(deck);
    if (!card) return null;
    if (face === 'back' && card.backFace) {
      return card.backFace.printings?.[0]?.image_url
        || card.printings?.[0]?.image_url
        || null;
    }
    return deckAvatarImageUrl(deck, this.props.sorceryCards);
  }

  flipAvatarFace = (deckId) => {
    this.setState((s) => {
      const current = s.avatarFace[deckId] || 'front';
      return {
        avatarFace: { ...s.avatarFace, [deckId]: current === 'front' ? 'back' : 'front' },
      };
    });
  };

  handleConfirm = () => {
    const decks = this.props.decks || PROGRESSION_DECKS;
    const deck = decks.find((d) => d.id === this.state.selected);
    if (!deck) return;
    // Guard against a stale selection. E.g. a successful claim that
    // just landed in ownedIds before componentDidUpdate has had a
    // chance to re-pick. The server would reject this anyway, but
    // refusing here avoids the dud "could not claim deck" toast.
    const ownedIds = new Set(this.props.ownedIds || []);
    if (ownedIds.has(deck.id)) return;
    (this.props.onChoose || this.props.onStarterChosen)?.(deck);
  };

  selectDeck = (deckId) => {
    if (this.state.selected === deckId) return;
    this.setState({ selected: deckId });
  };

  render() {
    const { selected, entryDone } = this.state;
    const decks = this.props.decks || PROGRESSION_DECKS;
    const ownedIds = new Set(this.props.ownedIds || []);
    const title = this.props.title || t('Choose Your Path');
    const subtitle = this.props.subtitle
      || t('Select a starter deck to begin your journey.');
    const confirmLabel = this.props.confirmLabel || t('Start with this deck');
    const selectedDeck = decks.find((d) => d.id === selected);
    // Treat an owned selection as no-selection for the confirm button.
    // Belt-and-braces with the handleConfirm guard. Keeps the disabled
    // state visible to the player.
    const canConfirm = Boolean(selected) && !ownedIds.has(selected);
    const selectedAvatarCard = selectedDeck ? this.getAvatarCard(selectedDeck) : null;
    const selectedAvatarFace = (selectedDeck && this.state.avatarFace[selectedDeck.id]) || 'front';
    const selectedAvatarUrl = selectedDeck
      ? this.getAvatarImageUrl(selectedDeck, selectedAvatarFace)
      : null;
    const selectedAvatarFlippable = Boolean(selectedAvatarCard?.backFace);

    return (
      <div className="fixed inset-0 z-[55] flex flex-col" style={{ background: BG_ATMOSPHERE }}>
        <style>{PICKER_KEYFRAMES}</style>
        {/* Stone / chisel / scratches / cracks texture wash. Same
            recipe as the FirstPlayerDiceModal panel (CONTENT_BG_DEFAULT
            with overlay-blend against a dark base in the SAME layer).
            Overlay against a near-opaque dark base is what the texture
            assets are actually authored for; using mix-blend-mode
            against a dark page produces no visible result because
            those blend formulas (soft-light especially) flatten on
            shadows. The 60% base lets the underlying BG_ATMOSPHERE
            warm-glow show through softly, so the screen still has
            depth instead of reading as a uniform stone slab. */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            background: `${CONTENT_BG_DEFAULT}, rgba(12, 10, 8, 0.6)`,
            backgroundBlendMode: 'overlay, normal',
          }}
        />
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="flex-1 flex flex-col items-center justify-between relative z-10 px-6 py-6">
          {/* Title in the medieval display font, sized as a proper
              page heading. Sits one tier below the hero deck name
              (3.2cqw gradient gold) by being smaller and in parchment
              rather than gradient gold, so the visual hierarchy reads
              page-title → deck-name → body without anything fighting
              for attention. Subtitle stays in the sans body font. */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1
              className="arena-heading"
              style={{
                fontSize: '2.8cqw',
                fontWeight: 700,
                letterSpacing: '0.04em',
                lineHeight: 1.05,
                color: `${PARCHMENT} 0.96)`,
                textShadow: [
                  '0 2px 6px rgba(0,0,0,0.85)',
                  `0 0 24px ${GOLD} 0.28)`,
                  '0 1px 0 rgba(255, 235, 195, 0.12)',
                ].join(', '),
                marginBottom: '0.3cqw',
              }}
            >
              {title}
            </h1>
            <OrnamentalDivider className="my-1 w-64 mx-auto" />
            <p
              className="text-xs mt-1"
              style={{ color: `${PARCHMENT} 0.55)`, letterSpacing: '0.04em' }}
            >
              {subtitle}
            </p>
          </motion.div>

          {/* Hero stage. Featured deck. Avatar card on the left at full
              size, big gradient-gold name + element pills + description
              on the right. AnimatePresence cross-fades both halves when
              the selection changes, so the stage transitions cinematically
              rather than just re-rendering. */}
          {selectedDeck ? (
            <div
              className="flex items-center justify-center gap-6 mx-auto"
              style={{ width: '90cqw', minHeight: '24cqw' }}
            >
              {/* Avatar. Keyed on deck.id so AnimatePresence treats each
                  selection swap as enter/exit, cross-fading the cards. */}
              <div className="relative shrink-0" style={{ width: '17cqw', height: '23.75cqw' }}>
                {/* Element-themed aura. AnimatePresence keyed by deck.id
                    cross-fades the gradient when the selection changes,
                    so the color doesn't snap when the user picks a
                    different deck. The inner div carries the actual
                    CSS keyframe breathe. Kept off the motion wrapper
                    so framer-motion's opacity transition doesn't fight
                    the animation's transform/opacity values. */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`aura-${selectedDeck.id}`}
                    aria-hidden
                    className="absolute pointer-events-none"
                    // Oversized container so the visible blob has lots
                    // of transparent margin around it. Even if some
                    // unknown parent applies a clip we'll never spot
                    // it, the clip happens far from the blob's edge
                    // and stays imperceptible.
                    style={{ inset: '-8cqw' }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div
                      className="w-full h-full"
                      style={{
                        background: elementHaloGradient(selectedDeck.elements),
                        // Real Gaussian blur on the gradient so any
                        // residual edge. Gradient stop, container
                        // bound, clip path. Is rendered impossible to
                        // see as a sharp line. This is what makes an
                        // aura read as torchlight rather than a clipped
                        // shape.
                        filter: 'blur(1.4cqw)',
                        animation: 'aspHaloBreathe 3.8s ease-in-out infinite',
                        willChange: 'transform, opacity',
                      }}
                    />
                  </motion.div>
                </AnimatePresence>
                {/* Contact shadow. Anchors the card to the stage. */}
                <div
                  aria-hidden
                  className="absolute pointer-events-none"
                  style={{
                    left: '6%',
                    right: '6%',
                    bottom: '-0.7cqw',
                    height: '1.1cqw',
                    background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)',
                    filter: 'blur(0.35cqw)',
                  }}
                />
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedDeck.id}
                    initial={{ opacity: 0, scale: 0.93, rotate: -1.5 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    exit={{ opacity: 0, scale: 0.93, rotate: 1.5 }}
                    transition={{ duration: 0.35, ease: [0.2, 0.9, 0.25, 1.05] }}
                    whileHover={{ scale: 1.025 }}
                    className="relative w-full h-full"
                    style={{
                      filter: `drop-shadow(0 12px 26px rgba(0,0,0,0.7)) drop-shadow(0 0 22px ${GOLD} 0.5))`,
                    }}
                  >
                    <button
                      type="button"
                      className="relative w-full h-full card-mask cursor-pointer bg-transparent border-0 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        // sorceryCards is normalized at load time by
                        // normalizeSorceryCards. The runtime shape is
                        // c.type, NOT c.guardian.type. The raw JSON
                        // nests under guardian; the prop we receive is
                        // already flattened.
                        if (selectedAvatarCard) this.setState({ inspectedCard: selectedAvatarCard });
                      }}
                      onMouseEnter={() => this.setState({ hoveredAvatar: true })}
                      onMouseLeave={() => this.setState({ hoveredAvatar: false })}
                      aria-label={t('Inspect {name} avatar', { name: selectedDeck.name })}
                    >
                      {selectedAvatarUrl ? (
                        <img
                          src={selectedAvatarUrl}
                          alt={selectedDeck.name}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center"
                          style={{ background: `${GOLD} 0.06)`, color: `${GOLD} 0.2)`, fontSize: '4cqw' }}
                        >
                          ?
                        </div>
                      )}
                    </button>
                    {selectedAvatarFlippable ? (
                      <CardFlipButton
                        face={selectedAvatarFace}
                        onFlip={() => this.flipAvatarFace(selectedDeck.id)}
                        size="2.4cqw"
                      />
                    ) : null}
                  </motion.div>
                </AnimatePresence>

                {/* Hover hint. Fades in beneath the avatar when the
                    cursor is over it, telling the player the inspect
                    affordance is available. Matches the hint that
                    appears beneath the fanned key cards. */}
                <AnimatePresence>
                  {this.state.hoveredAvatar ? (
                    <motion.div
                      className="absolute left-0 right-0 text-center pointer-events-none"
                      style={{
                        bottom: '-2.4cqw',
                        fontSize: '0.7cqw',
                        letterSpacing: '0.08em',
                        color: `${PARCHMENT} 0.55)`,
                        textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                      }}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.15 }}
                    >
                      {t('Left click or hit Space to inspect')}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>

              {/* Text panel. Slides + fades in from the right as the
                  avatar settles, giving the swap a cinematic "reveal"
                  feel rather than a hard cut. */}
              <div className="flex-1 min-w-0 flex flex-col" style={{ maxWidth: '32cqw' }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedDeck.id}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Deck name. Display-tier gradient-gold title,
                        matching the FirstPlayerDiceModal "Roll for
                        Initiative" treatment. */}
                    <h2
                      className="arena-heading"
                      style={{
                        fontSize: '3.2cqw',
                        fontWeight: 700,
                        letterSpacing: '0.02em',
                        lineHeight: 1.02,
                        background: 'linear-gradient(180deg, #fbe9b8 0%, #e8c466 55%, #b9893a 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.75))',
                        textShadow: [
                          '0 2px 8px rgba(0,0,0,0.85)',
                          `0 0 28px ${GOLD} 0.4)`,
                        ].join(', '),
                        marginBottom: '0.9cqw',
                      }}
                    >
                      {selectedDeck.name}
                    </h2>

                    {/* Element pills + strategy chip on one row.
                        pills lead with element identity, the strategy
                        chip tags the deck's archetype in two words.
                        Both read as "at-a-glance attributes." */}
                    <div className="flex gap-2 flex-wrap items-center mb-3">
                      {selectedDeck.elements.map((el) => {
                        const c = ELEMENT_COLORS[el] || { color: TEXT_MUTED, bg: 'rgba(180,140,60,0.18)', border: `${GOLD} 0.2)`, glow: `${GOLD} 0.2)`, rgb: '180,140,60' };
                        return (
                          <span
                            key={el}
                            className="rounded-full font-bold inline-flex items-center"
                            style={{
                              fontSize: '0.85cqw',
                              padding: '0.3cqw 1.1cqw 0.3cqw 0.65cqw',
                              gap: '0.45cqw',
                              color: c.color,
                              // Heavier element-tinted base + frosted
                              // backdrop blur so the pill reads as a
                              // solid token over the textured stage,
                              // not see-through text floating in space.
                              background: `rgba(${c.rgb},0.28)`,
                              backdropFilter: 'blur(8px) saturate(1.2)',
                              WebkitBackdropFilter: 'blur(8px) saturate(1.2)',
                              border: `1px solid ${c.border}`,
                              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 2px 8px rgba(0,0,0,0.35), 0 0 14px ${c.glow}`,
                            }}
                          >
                            <ElementIcon element={el} size="1.05cqw" />
                            {el}
                          </span>
                        );
                      })}
                      {selectedDeck.strategy ? (
                        <span
                          className="rounded-full font-bold inline-flex items-center"
                          style={{
                            fontSize: '0.7cqw',
                            // Vertical padding bumped to match the
                            // element pill's overall height. The
                            // element pill is dominated vertically by
                            // its 1.05cqw ElementIcon (taller than
                            // the 0.85cqw text), so the strategy
                            // chip. Which has no icon. Needs extra
                            // top/bottom padding to land at the same
                            // pill height.
                            padding: '0.55cqw 1cqw',
                            lineHeight: 1,
                            color: `${PARCHMENT} 0.95)`,
                            background: 'rgba(40, 28, 12, 0.55)',
                            backdropFilter: 'blur(8px) saturate(1.2)',
                            WebkitBackdropFilter: 'blur(8px) saturate(1.2)',
                            border: `1px solid ${GOLD} 0.5)`,
 boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 2px 8px rgba(0,0,0,0.4)',
 }}
 >
 {selectedDeck.strategy}
 </span>
 ) : null}
 </div>

 {/* Gold hairline rule. Chapter break between the
 masthead and the body copy. */}
 <div
 style={{
 height: '1px',
 background: `linear-gradient(90deg, ${GOLD} 0.5) 0%, ${GOLD} 0.15) 60%, transparent 100%)`,
                        width: '65%',
                        marginBottom: '0.85cqw',
                      }}
                    />

                    {/* Flavor description. Short "what is this deck" tag. */}
                    <p
                      className="leading-relaxed"
                      style={{
                        fontSize: '0.85cqw',
                        color: `${PARCHMENT} 0.7)`,
                        textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                        marginBottom: '0.9cqw',
                      }}
                    >
                      {selectedDeck.description}
                    </p>

                    {/* "How it plays" tutorial block. Practical, not
                        flavor. Section header in parchment caps with
                        wide tracking, body in the same family as the
                        flavor line so the eye groups them as one
                        document. */}
                    {selectedDeck.tutorial ? (
                      <div style={{ marginBottom: '1cqw' }}>
                        <div
                          style={{
                            fontSize: '0.55cqw',
                            color: `${GOLD} 0.75)`,
 fontWeight: 700,
 marginBottom: '0.4cqw',
 textShadow: '0 1px 2px rgba(0,0,0,0.6)',
 }}
 >
 {t('How It Plays')}
 </div>
 <p
 className="leading-relaxed"
 style={{
 fontSize: '0.85cqw',
 color: `${PARCHMENT} 0.82)`,
                            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                          }}
                        >
                          {selectedDeck.tutorial}
                        </p>
                      </div>
                    ) : null}

                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Fanned key-cards hand. Three signature cards rotated
                  outward from a shared bottom-center pivot, like a
                  player's hand. Hover lifts + straightens the focused
                  card; click opens the full CardInspector modal so the
                  player can read the rules text and inspect the art. */}
              {selectedDeck.keyCards?.length ? (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedDeck.id}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    transition={{ duration: 0.35, ease: [0.2, 0.9, 0.25, 1.05] }}
                    className="relative shrink-0"
                    // Extra marginLeft pushes the whole fan further
                    // right than the flex gap alone. Necessary
                    // because card 1's `-14°` rotation tilts its top
                    // edge ~4.5cqw left of its natural container
                    // origin (bottom-center pivot). Without this, the
                    // rotated corner overlaps the text panel's right
                    // edge. 5cqw of padding clears the rotation
                    // overshoot plus visual breathing room.
                    style={{ width: '29cqw', height: '21cqw', marginLeft: '5cqw' }}
                  >
                    {selectedDeck.keyCards.map((cardName, idx, arr) => {
                      const card = this.props.sorceryCards?.find((c) => c.name === cardName);
                      const imgUrl = card?.printings?.[0]?.image_url;
                      const isHovered = this.state.hoveredKeyCard === cardName;
                      // Fan math. Symmetric around the middle card.
                      // 3 cards → angles [-14, 0, +14]; offsets stack
                      // by ~half a card width so each subsequent card
                      // overlaps its neighbor.
                      const midIdx = (arr.length - 1) / 2;
                      const angle = (idx - midIdx) * 14;
                      const xOffset = idx * 7;
                      // Bundled into a single transform string so the
                      // browser interpolates rotate + translate + scale
                      // together on the compositor thread, exactly like
                      // the in-game hand fan (GameBoard.jsx renderHand).
                      // Spring-physics-via-framer is what was causing
                      // the stutter. CSS transitions on transform are
                      // GPU-accelerated and don't paint per frame.
                      const transform = isHovered
                        ? 'translateY(-0.8cqw) rotate(0deg) scale(1.06)'
                        : `translateY(0cqw) rotate(${angle}deg) scale(1)`;
                      return (
                        <button
                          key={cardName}
                          type="button"
                          className="absolute card-mask cursor-pointer bg-transparent border-0 p-0"
                          style={{
                            left: `${xOffset}cqw`,
                            top: '0.5cqw',
                            width: '14cqw',
                            height: '19.56cqw',
                            transformOrigin: 'bottom center',
                            zIndex: isHovered ? 30 : 10 + idx,
                            transition: 'transform 0.28s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                            transform,
                            willChange: 'transform',
                            filter: `drop-shadow(0 4px 10px rgba(0,0,0,0.6)) drop-shadow(0 0 4px ${GOLD} 0.2))`,
                          }}
                          onMouseEnter={() => this.setState({ hoveredKeyCard: cardName })}
                          onMouseLeave={() => this.setState({ hoveredKeyCard: null })}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (card) this.setState({ inspectedCard: card });
                          }}
                          aria-label={t('Inspect {name}', { name: cardName })}
                        >
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={cardName}
                              className="w-full h-full object-cover"
                              draggable={false}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: `${GOLD} 0.06)`, color: `${GOLD} 0.2)`, fontSize: '1cqw' }}>?</div>
                          )}
                        </button>
                      );
                    })}

                    {/* Floating caption for the hovered card. Name on
                        top, inspect-hint subline below. Only renders
                        while a card is hovered so the resting fan
                        stays clean. */}
                    <AnimatePresence>
                      {this.state.hoveredKeyCard ? (
                        <motion.div
                          key={this.state.hoveredKeyCard}
                          className="absolute left-0 right-0 text-center pointer-events-none"
                          style={{ bottom: '-2.6cqw' }}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 4 }}
                          transition={{ duration: 0.15 }}
                        >
                          <div
                            style={{
                              fontSize: '0.9cqw',
                              letterSpacing: '0.08em',
                              color: `${PARCHMENT} 0.95)`,
                              textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                              fontWeight: 600,
                            }}
                          >
                            {this.state.hoveredKeyCard}
                          </div>
                          <div
                            style={{
                              fontSize: '0.7cqw',
                              letterSpacing: '0.08em',
                              color: `${PARCHMENT} 0.55)`,
                              textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                              marginTop: '0.15cqw',
                            }}
                          >
                            {t('Left click or hit Space to inspect')}
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </motion.div>
                </AnimatePresence>
              ) : null}
            </div>
          ) : null}

          {/* Thumbnail strip. Every deck visible at a glance, click to
              swap the stage. Smaller card-masked avatars, no info; the
              hero above is the source of truth for everything that
              isn't the avatar art itself. */}
          <div className="flex gap-3 justify-center mx-auto">
            {decks.map((deck, i) => {
              const isOwned = ownedIds.has(deck.id);
              const isSelected = !isOwned && selected === deck.id;
              const avatarUrl = this.getAvatarImageUrl(deck);

              return (
                <motion.button
                  key={deck.id}
                  type="button"
                  disabled={isOwned}
                  className="relative cursor-pointer disabled:cursor-not-allowed bg-transparent border-0 p-0"
                  style={{
                    width: '9cqw',
                    height: '12.57cqw',
                    // Same corner as the card-mask clip below, so the
                    // box-shadow ring follows the rounded edge of the card
                    // art and doesn't leave visible corner wedges.
                    borderRadius: RADIUS.card,
                    opacity: isOwned ? 0.42 : 1,
                    filter: isOwned ? 'grayscale(0.55)' : 'none',
                    // Three-shadow stack with matching counts so CSS can
                    // interpolate smoothly between idle and selected:
                    //   1. spread ring → the "stroke"
                    //   2. blurred ring → the gold aura
                    //   3. drop shadow → grounds the card
                    boxShadow: isSelected
                      ? `0 0 0 0.13cqw ${ACCENT_GOLD}, 0 0 22px ${GOLD} 0.55), 0 5px 14px rgba(0,0,0,0.65)`
                      : `0 0 0 0.13cqw rgba(212,168,67,0), 0 0 0px ${GOLD} 0), 0 4px 10px rgba(0,0,0,0.55)`,
                    transition: 'box-shadow 240ms ease, opacity 200ms ease',
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: isOwned ? 0.42 : 1, y: 0 }}
                  transition={entryDone
                    ? { type: 'tween', duration: 0.15, ease: 'easeOut' }
                    : { type: 'spring', stiffness: 260, damping: 22, delay: 0.35 + i * 0.05 }
                  }
                  whileHover={isOwned ? undefined : { scale: 1.06, y: -3, transition: { type: 'tween', duration: 0.15, ease: 'easeOut' } }}
                  whileTap={isOwned ? undefined : { scale: 0.97 }}
                  onClick={() => !isOwned && this.selectDeck(deck.id)}
                >
                  <div className="relative w-full h-full card-mask">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={deck.name} className="w-full h-full object-cover" draggable={false} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: `${GOLD} 0.06)`, color: `${GOLD} 0.2)`, fontSize: '1.5cqw' }}>?</div>
                    )}

                    {isOwned ? (
                      <>
                        <div className="absolute inset-0 bg-black/40 pointer-events-none" />
                        <div
                          className="absolute inset-x-0 bottom-1.5 text-center"
                          style={{
                            fontSize: '0.7cqw',
                            color: `${PARCHMENT} 0.9)`,
                            textShadow: '0 1px 3px rgba(0,0,0,0.95)',
                            fontWeight: 700,
                          }}
                        >
                          {t('Owned')}
                        </div>
                      </>
                    ) : null}
                  </div>
                </motion.button>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <button
              type="button"
              disabled={!canConfirm}
              className="px-10 py-2.5 text-sm font-semibold arena-heading cursor-pointer transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              style={canConfirm ? GOLD_BTN : { ...BEVELED_BTN, color: TEXT_MUTED, borderRadius: 'var(--radius-md)' }}
              onMouseEnter={(e) => { if (canConfirm) { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 0 30px rgba(212,168,67,0.35), inset 0 1px 0 rgba(255,255,255,0.2)'; } }}
              onMouseLeave={(e) => { if (canConfirm) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = GOLD_BTN.boxShadow; } }}
              data-sound={UI.CONFIRM}
              onClick={this.handleConfirm}
            >
              {confirmLabel}
            </button>
          </motion.div>
        </div>

        {/* CardInspector. Opens when the player clicks a fanned key
            card. Portals into .app-canvas, so it letterboxes with the
            canvas like every other modal. */}
        {this.state.inspectedCard ? (() => {
          // When the inspected card is the avatar of the currently
          // featured deck, mirror its face state both ways so flipping
          // the hero card and flipping inside the inspector stay in
          // lock step. Key cards have no per-deck face state of their
          // own. They fall through to the default uncontrolled
          // inspector behaviour (front face, local-only flip).
          const isFeaturedAvatar =
            selectedDeck && this.state.inspectedCard.name === selectedDeck.name
              && this.state.inspectedCard.type === 'Avatar';
          return (
            <CardInspector
              card={this.state.inspectedCard}
              imageUrl={isFeaturedAvatar ? selectedAvatarUrl : this.state.inspectedCard.printings?.[0]?.image_url}
              sorceryCards={this.props.sorceryCards}
              initialFace={isFeaturedAvatar ? selectedAvatarFace : 'front'}
              onFlipChange={isFeaturedAvatar
                ? (face) => this.setState((s) => ({
                    avatarFace: { ...s.avatarFace, [selectedDeck.id]: face },
                  }))
                : undefined}
              onClose={() => this.setState({ inspectedCard: null })}
            />
          );
        })() : null}
      </div>
    );
  }
}
